import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, SectionHeader } from '@/components/ui/card';
import { ErrorNote, TableSkeleton } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { useSetPageTitle } from '@/components/page-title';
import { FilingStatusPill } from '@/features/filings/status-pill';
import { AmendFlow, type AmendKind } from './amend-flow';
import { filingQuery } from '@/lib/queries';
import { errorMessage } from '@/lib/api';
import { formatCount } from '@/lib/format';

interface PageCopy {
  title: string;
  lead: string;
  /** What the e-file has to allow before this page can do anything. */
  action: 'correct' | 'replace';
  refused: string;
  toastTitle: string;
  toastDetail: string;
}

const pageCopy: Record<AmendKind, PageCopy> = {
  correction: {
    title: 'Submit corrections',
    lead: 'A correction replaces records the IRS has already accepted on this e-file.',
    action: 'correct',
    refused:
      'This filing cannot be corrected yet. The IRS has to acknowledge an e-file, and any correction already sent has to finish, before another one can be submitted.',
    toastTitle: 'Corrections submitted',
    toastDetail: 'The corrected records are queued for the IRS.',
  },
  replacement: {
    title: 'Submit replacement',
    lead: 'A replacement resends an e-file the IRS rejected, inside the IRS grace window.',
    action: 'replace',
    refused:
      'This filing cannot be replaced. Only an e-file the IRS rejected can be replaced, and only once, within the IRS grace window.',
    toastTitle: 'Replacement submitted',
    toastDetail: 'The fixed records are queued for the IRS.',
  },
};

/**
 * The page around {@link AmendFlow}: it loads the e-file being amended, refuses
 * politely when Tab Service says the e-file does not allow this action, and takes
 * the customer to the new e-file once it is away.
 */
export function AmendPage({
  kind,
  filingId,
  resumeUploadId,
}: {
  kind: AmendKind;
  filingId: string;
  /** A draft of this amendment, reopened from the Drafts list (TS-8288). */
  resumeUploadId?: string;
}) {
  const words = pageCopy[kind];
  const navigate = useNavigate();
  const toast = useToast();
  const filing = useQuery(filingQuery(filingId));
  const data = filing.data;

  useSetPageTitle(words.title);

  const allowed = (data?.actions ?? []).includes(words.action);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/e-files/$efileId" params={{ efileId: filingId }}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to the e-file
          </Link>
        </Button>
      </div>

      <Card>
        <SectionHeader
          title={words.title}
          description={words.lead}
          actions={data ? <FilingStatusPill status={data.status} /> : null}
        />
        {filing.isPending ? (
          <TableSkeleton rows={2} columns={4} />
        ) : filing.isError ? (
          <ErrorNote title="Could not load this e-file" detail={errorMessage(filing.error)} />
        ) : data ? (
          <p className="text-base text-muted-foreground">
            Amending{' '}
            <Link to="/e-files/$efileId" params={{ efileId: filingId }} className="text-link hover:underline">
              {data.id}
            </Link>
            : {data.formType}, tax year {data.taxYear}, {formatCount(data.recordCount)} records.
          </p>
        ) : null}
      </Card>

      {data && !allowed ? (
        <Card>
          <ErrorNote
            title={`This filing is not open to ${kind === 'correction' ? 'corrections' : 'a replacement'}`}
            detail={words.refused}
            action={
              <Button asChild variant="outline" size="sm">
                <Link to="/e-files/$efileId" params={{ efileId: filingId }}>
                  Open the e-file
                </Link>
              </Button>
            }
          />
        </Card>
      ) : null}

      {data && allowed ? (
        <AmendFlow
          kind={kind}
          filing={data}
          resumeUploadId={resumeUploadId}
          onSubmitted={(created) => {
            toast.success(words.toastTitle, words.toastDetail);
            void navigate({ to: '/e-files/$efileId', params: { efileId: created.id } });
          }}
        />
      ) : null}
    </>
  );
}
